import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography, TextField, Snackbar, Alert, CircularProgress } from '@mui/material';
import { ListingOnboardingStepProps, ListingFormData } from '../../types/listing_onboarding'
import { useState, useEffect } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import axiosClient from '../../services/axiosClient';

const ListingOnboardingStep2: React.FC<ListingOnboardingStepProps> = ({ formData, setFormData, setStepValid }) => {
    const [isTyping, setIsTyping] = useState(false);
    const [isWaitingForAI, setIsWaitingForAI] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    const [pollingInterval, setPollingInterval] = useState<number | null>(null);
    const typingSpeed = 5; //lower # faster, higher

    // Add validation function
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        // Only show title error if field has been touched
        if (touched.title) {
            if (!formData.title || formData.title.trim() === '') {
                newErrors.title = 'Listing title is required.';
            }
        }

        // Only show description error if field has been touched
        if (touched.description) {
            if (!formData.description || formData.description.trim() === '') {
                newErrors.description = 'Listing description is required.';
            }
        }

        setErrors(newErrors);
    };

    // Run validation whenever form data or touched state changes
    useEffect(() => {
        validateForm();
    }, [formData, touched]);

    // Function to start typing animation
    const startTypingAnimation = (title: string, description: string) => {
        setIsTyping(true);

        // Animate title first
        let currentTitleIndex = 0;
        const titleInterval = setInterval(() => {
            setFormData(prev => ({
                ...prev,
                title: title.slice(0, currentTitleIndex + 1)
            }));
            currentTitleIndex++;

            if (currentTitleIndex >= title.length) {
                clearInterval(titleInterval);

                // Start description animation after title is done
                let currentDescIndex = 0;
                const descInterval = setInterval(() => {
                    setFormData(prev => ({
                        ...prev,
                        description: description.slice(0, currentDescIndex + 1)
                    }));
                    currentDescIndex++;

                    if (currentDescIndex >= description.length) {
                        clearInterval(descInterval);
                        setIsTyping(false);
                        // Clear the stored content after animation
                        sessionStorage.removeItem('ai_generated_content');
                    }
                }, typingSpeed);
            }
        }, typingSpeed);
    };

    // Function to check for AI generation completion
    const checkAIGeneration = async () => {
        try {
            // Check if AI generation is in progress by looking for a flag in sessionStorage
            const isGenerating = sessionStorage.getItem('ai_generation_in_progress');
            
            if (isGenerating === 'true') {
                setIsWaitingForAI(true);
                
                // Poll for completion every 2 seconds
                const interval = setInterval(async () => {
                    try {
                        // Check if the AI content is now available
                        const aiContent = sessionStorage.getItem('ai_generated_content');
                        if (aiContent) {
                            const parsedContent = JSON.parse(aiContent);
                            if (parsedContent && parsedContent.title && parsedContent.description) {
                                clearInterval(interval);
                                setPollingInterval(null);
                                setIsWaitingForAI(false);
                                sessionStorage.removeItem('ai_generation_in_progress');
                                
                                // Start typing animation with the new content
                                startTypingAnimation(parsedContent.title, parsedContent.description);
                            }
                        }
                    } catch (error) {
                        console.error('Error checking AI generation:', error);
                    }
                }, 500);

                setPollingInterval(interval);

                // Stop polling after 30 seconds (timeout)
                setTimeout(() => {
                    if (interval) {
                        clearInterval(interval);
                        setPollingInterval(null);
                        setIsWaitingForAI(false);
                        sessionStorage.removeItem('ai_generation_in_progress');
                    }
                }, 5000);
            }
        } catch (error) {
            console.error('Error checking AI generation:', error);
            setIsWaitingForAI(false);
        }
    };

    useEffect(() => {
        // Check for existing AI content first
        const aiContent = sessionStorage.getItem('ai_generated_content');
        if (aiContent) {
            const parsedContent = JSON.parse(aiContent);
            if (parsedContent && parsedContent.title && parsedContent.description) {
                // Start typing animation if we have AI content and fields are empty
                if (!formData.title && !formData.description) {
                    startTypingAnimation(parsedContent.title, parsedContent.description);
                }
            }
        } else {
            // If no AI content, check if generation is in progress
            checkAIGeneration();
        }

        // Cleanup function to clear polling interval
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, []);

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTitle = event.target.value;
        
        // Update form data
        setFormData(prevData => ({
            ...prevData,
            title: newTitle
        }));

        // Validate the input immediately if field has been touched
        if (touched.title) {
            const newErrors = { ...errors };
            
            if (!newTitle || newTitle.trim() === '') {
                newErrors.title = 'Listing title is required.';
            } else {
                delete newErrors.title; // Clear the error if validation passes
            }
            
            setErrors(newErrors);
        }
    };

    const handleTitleBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, title: true }));
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDescription = event.target.value;
        
        // Update form data
        setFormData(prevData => ({
            ...prevData,
            description: newDescription
        }));

        // Validate the input immediately if field has been touched
        if (touched.description) {
            const newErrors = { ...errors };
            
            if (!newDescription || newDescription.trim() === '') {
                newErrors.description = 'Listing description is required.';
            } else {
                delete newErrors.description; // Clear the error if validation passes
            }
            
            setErrors(newErrors);
        }
    };

    const handleDescriptionBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, description: true }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt:0 }}>
            <FormControl component="fieldset" sx={{ mt: 0, mb: '5rem', width: '100%' }}>
                <Typography variant="body1" sx={{mb: '0.8rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }} gutterBottom>
                    Listing Title 
                    {isTyping && <span style={{ color: '#666', marginLeft: '8px' }}> ✨ being created ...</span>}
                    {isWaitingForAI && (
                        <span style={{ color: '#666', marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                            🤖 AI is generating title... <CircularProgress size={16} sx={{ ml: 1 }} />
                        </span>
                    )}
                </Typography>
                <TextField
                    id="title"
                    fullWidth
                    rows={2}
                    multiline
                    name="title"
                    variant="outlined"
                    value={formData.title}
                    disabled={isTyping || isWaitingForAI}
                    placeholder={isWaitingForAI ? "AI is generating your listing title..." : ""}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    error={!!errors.title}
                    helperText={errors.title}
                    FormHelperTextProps={{
                        sx: {
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                        }
                    }}
                    sx={{
                        mt: 1,
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: (isTyping || isWaitingForAI) ? '#f5f5f5' : 'transparent',
                            '& input': {
                                color: 'black'
                            },
                            '& textarea': {
                                color: 'black'
                            },
                            '&.Mui-disabled input, &.Mui-disabled textarea': {
                                color: 'black',
                                WebkitTextFillColor: 'black'
                            }
                        }
                    }} />

                <Typography variant="body1" sx={{mt: '1.5rem', mb: '0.8rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }} gutterBottom>
                    Listing Description 
                    {isTyping && <span style={{ color: '#666', marginLeft: '8px' }}> ✨ typing...</span>}
                    {isWaitingForAI && (
                        <span style={{ color: '#666', marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                            🤖 AI is generating description... <CircularProgress size={16} sx={{ ml: 1 }} />
                        </span>
                    )}
                </Typography>
                <TextField
                    id="description"
                    name="description"
                    fullWidth
                    multiline
                    rows={16}
                    variant="outlined"
                    value={formData.description}
                    disabled={isTyping || isWaitingForAI}
                    placeholder={isWaitingForAI ? "AI is generating your listing description..." : ""}
                    onChange={handleDescriptionChange}
                    onBlur={handleDescriptionBlur}
                    error={!!errors.description}
                    helperText={errors.description}
                    FormHelperTextProps={{
                        sx: {
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                        }
                    }}
                    sx={{
                        mt: 1,
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: (isTyping || isWaitingForAI) ? '#f5f5f5' : 'transparent',
                            '& input': {
                                color: 'black'
                            },
                            '& textarea': {
                                color: 'black'
                            },
                            '&.Mui-disabled input, &.Mui-disabled textarea': {
                                color: 'black',
                                WebkitTextFillColor: 'black'
                            }
                        }
                    }}
                />
                <Typography variant="body2" sx={{ color: 'body1', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ fontSize: 16, mr: 0.5, color: 'black' }} />
                    AI can make mistakes. Please check info. Edit to your liking
                </Typography>
            </FormControl>
        </Box>
    );
};

export default ListingOnboardingStep2;